import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Sparkles, Book, HeartHandshake, Lock } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { BigButton, Card } from "../design/BigButton";
import { ASSETS } from "../lib/assets";
import "./landing.css";

/**
 * Post-signup paywall. Live Stripe Checkout is the only supported path;
 * sandbox mock-token flow is not used in the UI. If the mode probe fails,
 * we keep the buy button disabled with a retry link rather than falling
 * back to the legacy sandbox endpoint. Handles the /parent/purchase/success
 * return-URL by polling GET /purchases/checkout/session/{id} until Stripe
 * confirms `paid` (or the webhook has already granted premium).
 */
export function PurchaseGateScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refresh, logout } = useAuth();

  const [products, setProducts] = useState([]);
  const [paymentMode, setPaymentMode] = useState(null);   // null=loading, "live"|"sandbox"|"error"
  const [modeError, setModeError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [msg, setMsg] = useState(null);
  const [status, setStatus] = useState(user?.subscriptionStatus || "free");
  const returnedFromStripe = useRef(false);

  const query = new URLSearchParams(location.search);
  const sessionId = query.get("session_id");
  const canceled = query.get("canceled") === "true";
  const isSuccessPath = location.pathname.endsWith("/success");

  const goPostUnlock = useCallback(async () => {
    try {
      const { data } = await api.get("/profiles");
      if ((data.items || []).length > 0) {
        navigate("/profiles", { replace: true });
        return;
      }
    } catch (_e) { /* fall through */ }
    navigate("/register/setup", { replace: true });
  }, [navigate]);

  // Poll Stripe session on return
  useEffect(() => {
    if (!sessionId || returnedFromStripe.current) return;
    returnedFromStripe.current = true;
    let cancelled = false;
    let attempts = 0;
    setMsg("Confirming your payment…");
    setBusy(true);

    const poll = async () => {
      while (!cancelled && attempts < 12) {
        attempts += 1;
        try {
          const r = await api.get(`/purchases/checkout/session/${sessionId}`);
          if (r.data.subscriptionStatus === "premium" || r.data.paymentStatus === "paid") {
            setStatus("premium");
            await refresh();
            setMsg("Unlocked! Redirecting…");
            setTimeout(() => { goPostUnlock(); }, 500);
            return;
          }
        } catch (e) {
          setMsg(formatApiErrorDetail(e.response?.data?.detail || e.message));
          setBusy(false);
          return;
        }
        await new Promise((res) => setTimeout(res, 1500));
      }
      if (!cancelled) {
        setMsg("Still processing — refresh the page in a moment.");
        setBusy(false);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh, goPostUnlock]);

  // Mode fetch — retryable. Do NOT default to sandbox on failure; the
  // legacy sandbox endpoint is 410 in live mode.
  const fetchMode = useCallback(() => {
    setModeError(null);
    setPaymentMode(null);
    api.get("/purchases/mode")
      .then((r) => setPaymentMode(r.data.mode))
      .catch((e) => {
        setPaymentMode("error");
        setModeError(formatApiErrorDetail(e.response?.data?.detail || e.message));
      });
  }, []);

  // Initial load: mode + product + restore
  useEffect(() => {
    fetchMode();
    api.get("/purchases/products").then((r) => setProducts(r.data.items || [])).catch(() => setProducts([]));
    if (!sessionId) {
      api.post("/purchases/restore").then(async (r) => {
        setStatus(r.data.subscriptionStatus);
        if (r.data.subscriptionStatus === "premium") {
          await refresh();
          goPostUnlock();
        }
      }).catch(() => {});
    }
    if (canceled) setMsg("Checkout canceled — you can try again any time.");
  }, [sessionId, canceled, goPostUnlock, refresh, fetchMode]);

  async function buy(_productId) {
    // Guard: mode must be resolved AND live before we can start checkout.
    // We never call the legacy sandbox endpoint from the UI — real users
    // should never hit it, and hitting it in live mode returns 410.
    if (paymentMode !== "live") return;
    setBusy(true); setRedirecting(true); setMsg(null);
    try {
      const r = await api.post("/purchases/checkout", {});
      if (r.data.url) {
        window.location.href = r.data.url;
        return;   // leave `redirecting` true — page is navigating away
      }
      setMsg("Could not start Stripe checkout — no URL returned.");
      setRedirecting(false);
    } catch (e) {
      setMsg(formatApiErrorDetail(e.response?.data?.detail || e.message)
        || "Couldn't reach checkout. Try again.");
      setRedirecting(false);
    } finally {
      setBusy(false);
    }
  }

  const product = products[0] || null;
  const canBuy = paymentMode === "live" && !busy && !redirecting;
  let buttonLabel;
  if (redirecting) buttonLabel = "Redirecting to secure checkout…";
  else if (busy) buttonLabel = "Processing…";
  else if (paymentMode === null) buttonLabel = "Loading…";
  else if (paymentMode === "error") buttonLabel = "Purchase temporarily unavailable";
  else if (paymentMode !== "live") buttonLabel = "Purchase temporarily unavailable";
  else buttonLabel = product ? `Unlock for $${product.priceUsd.toFixed(2)}` : "Unlock";

  return (
    <div className="landing" data-testid="purchase-gate">
      <header className="landing__nav">
        <div className="landing__brand">
          <img src={ASSETS.logo} alt="With My Little EYE" className="landing__logo" />
          <b>With My Little EYE</b>
        </div>
        <nav aria-label="Account">
          <span data-testid="purchase-gate-email" style={{ opacity: 0.7 }}>{user?.email}</span>
          <button
            data-testid="purchase-gate-signout"
            onClick={logout}
            style={{ background: "transparent", border: "none", color: "inherit", cursor: "pointer", padding: "0.5rem 0.75rem", fontWeight: 600 }}
          >
            Sign out
          </button>
        </nav>
      </header>

      <section className="landing__hero" style={{ minHeight: "auto", paddingBottom: "3rem" }}>
        <motion.div
          className="landing__hero-copy"
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
        >
          <h1>One family. One unlock.</h1>
          <p className="landing__tagline">No demo. No ads. No renewals.</p>
          <p className="landing__lead">
            Your account is ready. Complete the one-time family unlock to start
            playing — every world, every level, every child profile on your
            account, forever.
          </p>

          {paymentMode === "live" && (
            <div
              data-testid="purchase-gate-live-banner"
              style={{
                display: "inline-block", marginTop: "0.75rem", padding: "0.4rem 0.9rem",
                background: "#0e7c66", color: "#fff", borderRadius: 999,
                fontSize: 12, fontWeight: 700, letterSpacing: 0.3,
              }}
            >
              Secure checkout by Stripe
            </div>
          )}

          {paymentMode === "error" && (
            <div
              data-testid="purchase-gate-mode-error"
              style={{
                display: "inline-block", marginTop: "0.75rem", padding: "0.4rem 0.9rem",
                background: "#8a0e18", color: "#fff", borderRadius: 999,
                fontSize: 12, fontWeight: 700, letterSpacing: 0.3,
              }}
              role="status"
            >
              Purchase temporarily unavailable
              {" · "}
              <button
                type="button"
                onClick={fetchMode}
                data-testid="purchase-gate-mode-retry"
                style={{
                  background: "transparent", border: "none", color: "#fff",
                  fontWeight: 700, textDecoration: "underline", cursor: "pointer", padding: 0,
                }}
              >
                Retry
              </button>
              {modeError && (
                <span style={{ opacity: 0.85, marginLeft: 8, fontWeight: 500 }}>
                  ({modeError})
                </span>
              )}
            </div>
          )}

          <div data-testid="purchase-gate-product" style={{ marginTop: "2rem" }}>
            {status === "premium" ? (
              <Card>
                <h3>You&apos;re unlocked ✓</h3>
                <p>Taking you inside…</p>
                <BigButton onClick={goPostUnlock} data-testid="purchase-gate-continue">Continue</BigButton>
              </Card>
            ) : isSuccessPath && sessionId ? (
              <Card>
                <h3>Confirming your payment…</h3>
                <p>Hang tight while Stripe finalises your unlock.</p>
                {msg && <p data-testid="purchase-gate-msg" style={{ marginTop: "0.75rem" }}>{msg}</p>}
              </Card>
            ) : product ? (
              <Card>
                <h3>{product.name}</h3>
                <p>{product.description}</p>
                <p style={{ fontSize: "2rem", fontWeight: 800, margin: "0.5rem 0" }}>
                  ${product.priceUsd.toFixed(2)} <span style={{ fontSize: "0.9rem", fontWeight: 500, opacity: 0.7 }}>one-time</span>
                </p>
                <BigButton
                  onClick={() => buy(product.productId)}
                  disabled={!canBuy}
                  data-testid={`purchase-gate-buy-${product.productId}`}
                  aria-busy={busy || redirecting}
                >
                  {buttonLabel}
                </BigButton>
                {msg && <p data-testid="purchase-gate-msg" style={{ marginTop: "0.75rem" }}>{msg}</p>}
              </Card>
            ) : (
              <Card data-testid="purchase-gate-product-loading">
                <p>Loading the family unlock…</p>
              </Card>
            )}
          </div>

          <ul className="landing__badges" aria-label="Highlights" style={{ marginTop: "1.5rem" }}>
            <li><Sparkles size={18} /> Every world unlocked</li>
            <li><ShieldCheck size={18} /> No ads · no tracking</li>
            <li><HeartHandshake size={18} /> 30-day refund</li>
          </ul>
        </motion.div>
        <motion.div
          className="landing__hero-art"
          initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1, duration: 0.4 }}
        >
          <img src={ASSETS.scene} alt="Sample Toy Store scene from With My Little EYE" />
          <img src={ASSETS.mascot} alt="Ollie the owl mascot" className="landing__hero-mascot" />
        </motion.div>
      </section>

      <section className="landing__section landing__benefits">
        <h2>What&apos;s inside the family unlock</h2>
        <div className="landing__benefit-grid">
          <Card><h3><Book size={22} /> 5 worlds, 30+ levels</h3><p>Toy Store, Home, Around Town, Imagination Island, School.</p></Card>
          <Card><h3><Sparkles size={22} /> Every child profile</h3><p>Add as many kids on one parent account. No per-child upsell.</p></Card>
          <Card><h3><Lock size={22} /> Parent-gated</h3><p>All settings live behind a 4-6 digit PIN. Kids can&apos;t bounce out to billing.</p></Card>
        </div>
      </section>
    </div>
  );
}
